/**
 * "Pick the QuickBooks customer" — the one-tap chooser for a push that found
 * more than one QuickBooks customer with the same name (#qbo-customer-pick).
 *
 * Shows each ACTIVE QuickBooks customer with that name: name, email, balance.
 * Tapping one saves the link (it never creates a QuickBooks customer) and hands
 * control back so the caller can push again. "Try again" re-reads QuickBooks
 * right now — for when the office fixed the duplicate in QuickBooks itself.
 */
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Mail, RefreshCw, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { InfoButton } from "@/components/info-button";
import { formatDollars } from "@/lib/utils";
import {
    QBO_CUSTOMER_PICK_HELP,
    needsQboCustomerPick,
    type QboCustomerCandidate,
    type QboCustomerChoices,
} from "@/lib/qboCustomerPick";

export function QboCustomerPicker({
    open,
    customerId,
    invoiceId,
    onClose,
    onLinked,
    onTryAgain,
    retrying = false,
}: {
    open: boolean;
    /** Either the Groundwork customer, or the invoice whose customer it is. */
    customerId?: string | null;
    invoiceId?: string | null;
    onClose: () => void;
    /** Called after a customer is saved. Push again from here. */
    onLinked: (picked: QboCustomerCandidate) => void;
    /** Push again without picking (the duplicate was fixed in QuickBooks). */
    onTryAgain?: () => void;
    retrying?: boolean;
}) {
    const qs = customerId ? `customerId=${customerId}` : invoiceId ? `invoiceId=${invoiceId}` : "";
    const choices = useQuery({
        queryKey: ["qbo-customer-choices", qs],
        queryFn: () => api.get<QboCustomerChoices>(`/api/integrations/quickbooks/customer-choices?${qs}`),
        enabled: open && !!qs,
        staleTime: 0,
        gcTime: 0,
        retry: false,
    });
    const link = useMutation({
        mutationFn: (qboCustomerId: string) =>
            api.post<{ linked: QboCustomerCandidate }>("/api/integrations/quickbooks/link-customer", {
                customerId: choices.data?.customer.id,
                qboCustomerId,
            }),
        onSuccess: (r) => {
            toast.success(`Saved. ${r.linked.name} is the QuickBooks customer from now on.`);
            onLinked(r.linked);
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't save that. Try again."),
    });

    const data = choices.data;
    const list = data?.candidates ?? [];
    return (
        <Dialog open={open} onClose={onClose} title="Pick the QuickBooks customer">
            <div className="space-y-3" data-testid="qbo-customer-picker">
                <div className="flex items-start gap-2">
                    <p className="flex-1 text-[14px] leading-snug">
                        {data ? (
                            list.length > 1 ? (
                                <>
                                    QuickBooks has <b>{list.length}</b> customers named <b>{data.customer.name}</b>. Tap
                                    the right one.
                                </>
                            ) : list.length === 1 ? (
                                <>
                                    QuickBooks now has just one <b>{data.customer.name}</b>. Tap it to use it.
                                </>
                            ) : (
                                <>
                                    QuickBooks has no active customer named <b>{data.customer.name}</b>. Fix the name in
                                    QuickBooks, then tap Try again.
                                </>
                            )
                        ) : (
                            "Checking QuickBooks…"
                        )}
                    </p>
                    <InfoButton topic={QBO_CUSTOMER_PICK_HELP} />
                </div>

                {choices.isLoading && (
                    <div className="flex items-center gap-2 text-sm text-foreground/60">
                        <Loader2 className="h-4 w-4 animate-spin" /> Checking QuickBooks…
                    </div>
                )}
                {choices.isError && (
                    <p className="rounded-md border border-red-300 bg-red-50 p-2.5 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-200">
                        {(choices.error as any)?.body?.message ?? "Couldn't reach QuickBooks just now."}
                    </p>
                )}

                <ul className="space-y-2">
                    {list.map((c) => {
                        const isLinked = data?.linked?.id === c.id;
                        return (
                            <li key={c.id}>
                                <button
                                    type="button"
                                    disabled={link.isPending}
                                    onClick={() => link.mutate(c.id)}
                                    data-testid="qbo-customer-choice"
                                    className="flex w-full items-center gap-3 rounded-lg border-2 border-border bg-card p-3 text-left transition-colors hover:border-[#004AAD] hover:bg-sky-50 focus-visible:border-[#004AAD] focus-visible:outline-none disabled:opacity-60 dark:hover:bg-sky-950/30"
                                >
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-[15px] font-bold">{c.name}</span>
                                        <span className="mt-0.5 flex items-center gap-1.5 truncate text-[13px] text-foreground/70">
                                            <Mail className="h-3.5 w-3.5 flex-none" aria-hidden />
                                            {c.email ?? "No email in QuickBooks"}
                                        </span>
                                    </span>
                                    <span className="flex-none text-right">
                                        <span className="block text-[11px] uppercase tracking-wide text-foreground/55">
                                            Owes
                                        </span>
                                        <span className="block text-[15px] font-bold tabular-nums">
                                            {c.balance == null ? "—" : formatDollars(c.balance)}
                                        </span>
                                    </span>
                                    {isLinked && (
                                        <CheckCircle2
                                            className="h-5 w-5 flex-none text-emerald-600"
                                            aria-label="Linked now"
                                        />
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ul>

                <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                    <span className="text-[12px] text-foreground/60">Fixed it in QuickBooks?</span>
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={choices.isFetching || retrying || link.isPending}
                        onClick={() => {
                            if (onTryAgain) onTryAgain();
                            else void choices.refetch();
                        }}
                        data-testid="qbo-customer-try-again"
                    >
                        {choices.isFetching || retrying ? (
                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <RefreshCw className="mr-1 h-3.5 w-3.5" />
                        )}
                        Try again
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}

/**
 * The invoice page's way in: a queued push that parked because QuickBooks has
 * more than one customer with this name shows one plain button. Renders
 * nothing otherwise.
 */
export function QboCustomerPickButton({
    lastError,
    invoiceId,
    customerId,
    onRetry,
    retrying = false,
}: {
    lastError: string | null | undefined;
    /** The invoice whose customer it is (invoice page)… */
    invoiceId?: string | null;
    /** …or the Groundwork customer itself (a customer row on the sync issues list). */
    customerId?: string | null;
    /** Queue the push again (the server re-checks QuickBooks then). */
    onRetry: () => void;
    retrying?: boolean;
}) {
    const [open, setOpen] = useState(false);
    if (!needsQboCustomerPick(lastError)) return null;
    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="qbo-customer-pick-button"
                className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-2.5 py-0.5 text-xs font-bold text-white hover:bg-amber-600"
            >
                <Users className="h-3.5 w-3.5" aria-hidden /> Pick the right QuickBooks customer
            </button>
            <QboCustomerPicker
                open={open}
                invoiceId={customerId ? null : invoiceId}
                customerId={customerId ?? null}
                onClose={() => setOpen(false)}
                retrying={retrying}
                onLinked={() => {
                    setOpen(false);
                    onRetry();
                }}
                onTryAgain={() => {
                    setOpen(false);
                    onRetry();
                }}
            />
        </>
    );
}
